package com.anjuros.website.redesign.styles

import com.anjuros.website.redesign.theme.RdColor
import com.anjuros.website.redesign.theme.RdFont
import com.anjuros.website.redesign.theme.RdSize
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.hover
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px

// ---------- Hero: example search result ----------

val SeoHeroGridStyle = CssStyle {
    base { grid("1fr").alignItems(AlignItems.Center).css("gap" to "clamp(32px, 5vw, 64px)") }
    Breakpoint.LG { Modifier.css("grid-template-columns" to "1fr 1fr") }
}

val SeoSerpStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Grid).gap(14.px).backgroundColor(RdColor.Paper).border(1.px, LineStyle.Solid, RdColor.Line)
        .borderRadius(18.px).css("padding" to "18px", "min-width" to "0", "box-shadow" to RdSize.SHADOW)
}

val SeoSerpBarStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Flex).alignItems(AlignItems.Center).gap(10.px).color(RdColor.Muted)
            .border(1.px, LineStyle.Solid, RdColor.Line).borderRadius(999.px).css("padding" to "11px 16px", "font-size" to ".98rem")
    }
    cssRule(" span") { Modifier.color(RdColor.Ink).css("min-width" to "0", "overflow" to "hidden", "text-overflow" to "ellipsis", "white-space" to "nowrap") }
}

val SeoSerpMetaStyle = CssStyle.base {
    Modifier.color(RdColor.Muted).css("font-size" to ".78rem", "padding-left" to "4px")
}

val SeoResultStyle = CssStyle {
    base {
        Modifier.position(Position.Relative).display(DisplayStyle.Grid).gap(4.px).backgroundColor(RdColor.SkySoft)
            .border(1.px, LineStyle.Solid, RdColor.LogoBlue).borderRadius(12.px).css("padding" to "14px 16px", "min-width" to "0")
    }
}

val SeoResultSiteStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Flex).alignItems(AlignItems.Center).gap(8.px).color(RdColor.Muted).css("font-size" to ".8rem") }
    cssRule(" i") {
        Modifier.display(DisplayStyle.Grid).backgroundColor(RdColor.Brand).color(RdColor.BrandInk).borderRadius(50.px)
            .css("flex" to "none", "width" to "22px", "height" to "22px", "place-items" to "center", "font" to "700 .62rem/1 ${RdFont.BODY_STACK}", "font-style" to "normal")
    }
}

val SeoResultTitleStyle = CssStyle.base {
    Modifier.color(RdColor.Brand).css("font-size" to "1.08rem", "font-weight" to 600, "line-height" to 1.3)
}

val SeoResultTextStyle = CssStyle.base {
    Modifier.color(RdColor.Muted).css("font-size" to ".88rem", "line-height" to 1.45)
}

val SeoResultTagStyle = CssStyle.base {
    Modifier.position(Position.Absolute).backgroundColor(RdColor.Brand).color(RdColor.BrandInk).borderRadius(999.px)
        .css("top" to "-10px", "right" to "12px", "padding" to "6px 8px", "font" to "500 .66rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".08em", "text-transform" to "uppercase")
}

/** Grey bars standing in for the other search results. */
val SeoGhostResultStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(7.px).css("padding" to "4px 16px") }
    cssRule(" span") { Modifier.backgroundColor(RdColor.Line).borderRadius(5.px).css("height" to "9px", "width" to "90%") }
    cssRule(" span:first-child") { Modifier.css("width" to "35%") }
    cssRule(" span:nth-child(2)") { Modifier.css("width" to "70%", "height" to "12px") }
}

val SeoNotesStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Flex).gap(8.px).css("flex-wrap" to "wrap", "padding-top" to "4px", "border-top" to "1px dashed rgb(220,227,236)", "list-style" to "none", "padding-left" to "0") }
    cssRule(" li") {
        Modifier.color(RdColor.Brand).backgroundColor(RdColor.Paper).border(1.px, LineStyle.Solid, RdColor.LogoBlue).borderRadius(999.px)
            .css("padding" to "6px 9px", "font" to "500 .7rem/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".06em", "text-transform" to "uppercase")
    }
}

// ---------- Definition band ----------

val SeoDefineGridStyle = CssStyle {
    base { grid("1fr").css("gap" to "16px 56px", "align-items" to "start") }
    Breakpoint.MD { Modifier.css("grid-template-columns" to "auto 1fr") }
}

val SeoTermStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(6.px) }
    cssRule(" b") {
        Modifier.color(RdColor.Brand).fontFamily(*RdFont.DISPLAY)
            .css("font-size" to "clamp(2.4rem, 1.6rem + 3vw, 3.6rem)", "font-weight" to 900, "font-stretch" to "118%", "line-height" to 1)
    }
    cssRule(" span") {
        Modifier.color(RdColor.Muted).css("font" to "500 .74rem/1.4 ${RdFont.MONO_STACK}", "letter-spacing" to ".1em", "text-transform" to "uppercase")
    }
}

val SeoDefineTextStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(10.px).css("max-width" to "64ch") }
    cssRule(" h2") { Modifier.css("font-size" to "clamp(1.3rem, 1rem + 1vw, 1.7rem)") }
    cssRule(" p") { Modifier.color(RdColor.Muted) }
}

// ---------- Checklist ----------

val SeoChecklistStyle = CssStyle {
    base { grid("1fr").css("column-gap" to "48px", "list-style" to "none", "padding" to "0", "border-top" to "2px solid rgb(30,40,52)") }
    Breakpoint.MD { Modifier.css("grid-template-columns" to "1fr 1fr") }
    cssRule(" li") { Modifier.display(DisplayStyle.Flex).gap(14.px).css("padding" to "20px 0", "border-bottom" to "1px solid rgb(220,227,236)") }
    cssRule(" li > div") { Modifier.display(DisplayStyle.Grid).gap(4.px) }
    cssRule(" h3") { Modifier.css("font-size" to "1.1rem") }
    cssRule(" p") { Modifier.color(RdColor.Muted).css("font-size" to ".95rem") }
}

/** Solid blue tick box in front of each checklist item. */
val SeoTickStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Grid).backgroundColor(RdColor.Brand).color(RdColor.BrandInk).borderRadius(7.px)
        .css("flex" to "none", "width" to "26px", "height" to "26px", "place-items" to "center")
}

// ---------- Certificate ----------

val SeoCertGridStyle = CssStyle {
    base { grid("1fr").alignItems(AlignItems.Center).css("gap" to "clamp(28px, 5vw, 64px)") }
    Breakpoint.MD { Modifier.css("grid-template-columns" to "1fr 1fr") }
}

val SeoCertDetailsStyle = CssStyle {
    base { grid("auto 1fr").css("gap" to "8px 18px", "margin" to "0", "font-size" to ".95rem") }
    cssRule(" dt") { Modifier.color(RdColor.Muted).css("font" to "500 .72rem/1.7 ${RdFont.MONO_STACK}", "letter-spacing" to ".08em", "text-transform" to "uppercase") }
    cssRule(" dd") { Modifier.css("margin" to "0") }
}

/** The certificate image, slightly tilted until hovered. */
val SeoCertImageStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Block).border(1.px, LineStyle.Solid, RdColor.Line).borderRadius(14.px)
            .css("overflow" to "hidden", "max-width" to "100%", "transform" to "rotate(-1.5deg)", "transition" to "transform .25s", "box-shadow" to RdSize.SHADOW)
    }
    hover { Modifier.css("transform" to "rotate(0)") }
    cssRule(" img") { Modifier.display(DisplayStyle.Block).css("width" to "100%", "height" to "auto") }
}

// ---------- Statement band ----------

val SeoStatementStyle = CssStyle {
    base { Modifier.backgroundColor(RdColor.Band).color(RdColor.BandText).css("padding-block" to "clamp(56px, 7vw, 96px)") }
    cssRule(" .rd-eyebrow") { Modifier.color(RdColor.BandMuted) }
}

val SeoStatementStackStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(18.px).css("max-width" to "900px") }
    cssRule(" > p:last-child") { Modifier.color(RdColor.BandMuted).css("max-width" to "60ch", "font-size" to "1.08rem") }
}

val SeoStatementBigStyle = CssStyle {
    base {
        Modifier.fontFamily(*RdFont.DISPLAY).css(
            "font-size" to "clamp(1.6rem, 1rem + 2.4vw, 2.7rem)", "font-weight" to 800, "font-stretch" to "112%",
            "line-height" to 1.15, "letter-spacing" to "-.01em", "text-wrap" to "balance"
        )
    }
    cssRule(" span") { Modifier.color(RdColor.LogoBlue) }
}

// ---------- Ongoing SEO ----------

val SeoOngoingStyle = CssStyle {
    base {
        grid("1fr").alignItems(AlignItems.Center).backgroundColor(RdColor.Paper).border(1.px, LineStyle.Solid, RdColor.Line)
            .borderRadius(20.px).css("gap" to "20px 48px", "padding" to "clamp(24px, 4vw, 40px)")
    }
    Breakpoint.MD { Modifier.css("grid-template-columns" to "1.3fr 1fr") }
    cssRule(" p") { Modifier.color(RdColor.Muted) }
    cssRule(" ul") { Modifier.display(DisplayStyle.Grid).gap(6.px).color(RdColor.Muted).css("padding-left" to "1.1em") }
    cssRule(" h2") { Modifier.css("font-size" to "clamp(1.5rem, 1.1rem + 1.4vw, 2.1rem)") }
}

val SeoStackStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Grid).gap(14.px) }
    cssRule(" > a") { Modifier.css("justify-self" to "start") }
}
